import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Heart, Quote, Plus, MapPin, BookHeart } from "lucide-react";

function meta(s) {
  return [s.country, s.weddingDate && new Date(s.weddingDate).getFullYear()].filter(Boolean).join(" • ");
}

export default function Stories() {
  const [stories, setStories] = useState(null);
  const [form, setForm] = useState({ names: "", headline: "", story: "" });
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/stories"); setStories(data.stories || data.items || []); }
    catch { setStories([]); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.names || !form.story) return toast.error("Please add your names and story");
    try { await api.post("/stories", form); toast.success("Thank you! Your story will appear after review."); setOpen(false); setForm({ names: "", headline: "", story: "" }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const featured = stories?.[0];
  const rest = stories?.slice(1) || [];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-400 to-amber-500 text-white shadow-sm"><BookHeart className="h-6 w-6" /></div>
          <div>
            <h1 className="font-serif text-3xl font-bold text-emerald-950 sm:text-4xl">Success Stories</h1>
            <p className="text-sm text-slate-500">Real couples who found their spouse on Nikah Match</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="share-story-btn" className="rounded-full bg-amber-600 text-white hover:bg-amber-700"><Plus className="mr-1.5 h-4 w-4" /> Share your story</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif text-2xl">Share your success story</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2"><Label>Your names (e.g. Omar &amp; Maryam)</Label><Input value={form.names} onChange={(e) => setForm({ ...form, names: e.target.value })} data-testid="story-partner-input" /></div>
              <div className="space-y-2"><Label>Headline (optional)</Label><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} data-testid="story-headline-input" /></div>
              <div className="space-y-2"><Label>Your story</Label><Textarea rows={5} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} data-testid="story-text-input" placeholder="Tell us how you met and found barakah…" /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="story-submit-btn" className="bg-emerald-800 text-white hover:bg-emerald-900">Submit for review</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {stories === null ? (
        <div className="grid gap-6 lg:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64 rounded-3xl" />)}</div>
      ) : stories.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-emerald-300 bg-white py-20 text-center" data-testid="stories-empty">
          <Heart className="mb-3 h-10 w-10 text-rose-400" />
          <h3 className="font-serif text-2xl font-semibold text-emerald-900">Be the first to share</h3>
          <p className="mt-2 max-w-sm text-slate-500">Approved success stories will appear here to inspire others, in shaa Allah.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="stories-grid">
          {/* Featured */}
          {featured && (
            <div className="grid overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm animate-fade-up md:grid-cols-2">
              <div className="relative min-h-56 bg-emerald-900">
                {featured.photo ? <img src={featured.photo} alt={featured.names} className="h-full w-full object-cover" /> : <div className="flex h-full min-h-56 w-full items-center justify-center bg-gradient-to-br from-emerald-800 to-emerald-950 text-6xl font-serif text-amber-300/50"><Heart className="h-16 w-16 fill-current text-rose-400/70" /></div>}
                <span className="absolute left-4 top-4 rounded-full bg-rose-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Featured</span>
              </div>
              <div className="flex flex-col justify-center p-7 sm:p-9">
                <Quote className="h-8 w-8 text-amber-400" />
                {featured.headline && <p className="mt-3 font-serif text-2xl font-bold leading-snug text-emerald-900">{featured.headline}</p>}
                {(featured.preview || featured.story) && <p className="mt-3 leading-relaxed text-slate-700">{featured.preview || featured.story}</p>}
                <div className="mt-5 flex items-center gap-3 border-t border-emerald-900/10 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-mint text-emerald-700"><Heart className="h-4 w-4 fill-current" /></span>
                  <div>
                    <p className="font-serif text-lg font-semibold text-emerald-900">{featured.names}</p>
                    {meta(featured) && <p className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {meta(featured)}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rest */}
          {rest.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((s, i) => (
                <div key={s.id || i} className="flex flex-col overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  {s.photo && <img src={s.photo} alt={s.names} className="h-44 w-full object-cover" />}
                  <div className="flex flex-1 flex-col p-6">
                    <Quote className="h-7 w-7 text-amber-400" />
                    {s.headline && <p className="mt-2 font-serif text-lg font-semibold text-emerald-900">{s.headline}</p>}
                    {(s.preview || s.story) && <p className="mt-2 leading-relaxed text-slate-700">{s.preview || s.story}</p>}
                    <div className="mt-auto border-t border-emerald-900/10 pt-3">
                      <p className="font-serif text-lg font-semibold text-emerald-900">{s.names}</p>
                      {meta(s) && <p className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {meta(s)}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
